import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiUser, FiCheckCircle, FiShield, FiStar, FiBookOpen } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Hero = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  return (
    <div className="relative min-h-screen pt-32 pb-20 flex items-center bg-slate-950 overflow-hidden" id="get-offer">
      {/* Premium Background Elements */}
      <div className="absolute inset-0 z-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=2689&auto=format&fit=crop')" }} />
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-emerald-900/40" />
      
      {/* Decorative Glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-[120px] pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Copy */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-7 text-white pr-0 lg:pr-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-8 shadow-xl">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
              <span className="text-sm font-semibold text-emerald-100 tracking-wide uppercase">Real Estate Education Experts</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-[1.1] mb-6 tracking-tight">
              Master Land Flipping. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-200">
                Build True Wealth.
              </span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed font-light">
              Toptier Ventures LLC is dedicated to helping homeowners and business owners educate themselves on the real estate industry. Discover proven strategies to make money flipping land with our premium products and services.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
              {[
                "Comprehensive Video Courses",
                "1-on-1 Expert Mentorship",
                "Proven Deal Analysis Tools",
                "Exclusive Student Community"
              ].map((text, i) => (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + (i * 0.1) }}
                  key={i} 
                  className="flex items-center gap-3 bg-white/5 p-3 rounded-xl backdrop-blur-sm border border-white/10"
                >
                  <SafeIcon icon={FiCheckCircle} className="text-emerald-400 text-xl flex-shrink-0" />
                  <span className="text-slate-200 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex items-center gap-4 text-slate-400 text-sm">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-slate-400 text-xs" />
                  </div>
                ))}
              </div>
              <div className="flex flex-col">
                <div className="flex text-emerald-400 text-xs">
                  {[1, 2, 3, 4, 5].map(i => <SafeIcon key={i} icon={FiStar} className="fill-current" />)}
                </div>
                <span>Trusted by hundreds of students</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Premium Lead Form */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <div className="bg-white rounded-3xl shadow-2xl shadow-emerald-900/20 overflow-hidden border border-slate-100">
              <div className="bg-slate-50 border-b border-slate-100 p-6 sm:px-8 sm:pt-8 sm:pb-6">
                <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Book a Strategy Call</h2>
                <p className="text-slate-500 text-sm">Find out if our education programs are right for you.</p>
              </div>

              <div className="p-6 sm:p-8">
                {isSubmitted ? (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                      <SafeIcon icon={FiCheckCircle} className="text-emerald-600 text-4xl" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Request Received!</h3>
                    <p className="text-slate-600 leading-relaxed">
                      Thank you. An education advisor from Toptier Ventures LLC will contact you shortly to schedule your personalized strategy call.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Current Experience Level</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <SafeIcon icon={FiBookOpen} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                        </div>
                        <select required className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-900 appearance-none">
                          <option value="" disabled selected>Select your experience...</option>
                          <option value="beginner">Beginner (No deals yet)</option>
                          <option value="intermediate">Intermediate (1-5 deals)</option>
                          <option value="advanced">Advanced (5+ deals)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name</label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <SafeIcon icon={FiUser} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                          </div>
                          <input type="text" required className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-900" placeholder="John Doe" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
                        <div className="relative group">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <SafeIcon icon={FiPhone} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                          </div>
                          <input type="tel" required className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-900" placeholder="(555) 123-4567" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <SafeIcon icon={FiMail} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                        </div>
                        <input type="email" required className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-900" placeholder="john@example.com" />
                      </div>
                    </div>

                    <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 mt-2">
                      <div className="flex items-start">
                        <div className="flex items-center h-5 mt-0.5">
                          <input id="sms-consent" name="sms-consent" type="checkbox" required className="w-4 h-4 text-emerald-600 border-emerald-300 rounded focus:ring-emerald-500 cursor-pointer" />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="sms-consent" className="font-bold text-slate-800 cursor-pointer flex items-center gap-1.5">
                            <SafeIcon icon={FiShield} className="text-emerald-600" />
                            Explicit SMS Consent Required
                          </label>
                          <p className="text-slate-500 text-[11px] leading-relaxed mt-1.5">
                            By checking this box, you explicitly agree to receive SMS communications from Toptier Ventures LLC regarding educational programs and strategy calls. Message and data rates may apply. Reply HELP for help or STOP to cancel.
                          </p>
                        </div>
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      className="group relative w-full flex justify-center py-4 px-4 border border-transparent rounded-xl shadow-lg shadow-emerald-600/30 text-lg font-bold text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all overflow-hidden mt-6"
                    >
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-emerald-400/0 via-white/20 to-emerald-400/0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                      <span className="relative">Schedule My Strategy Call</span>
                    </button>
                    
                    <p className="text-center text-xs text-slate-400 font-medium mt-4 flex items-center justify-center gap-1">
                      <SafeIcon icon={FiShield} /> 100% Secure & Confidential
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default Hero;